import type PostHog from 'posthog-js'

import {
  PublicKeyCredentialRequestOptionsJSON,
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from '@simplewebauthn/typescript-types'

declare global {
  interface PublicKeyCredentialConstructor {
    signalUnknownCredential?(data: { rpId: string; credentialId: string }): void
    signalAllAcceptedCredentials?(data: {
      rpId: string
      userId: string
      allAcceptedCredentialIds: string[]
    }): void
    signalCurrentUserDetails?(data: {
      rpId: string
      userId: string
      name: string
      displayName: string
    }): void
  }

  interface Window {
    PublicKeyCredential: PublicKeyCredentialConstructor
    posthog: typeof PostHog
  }
}

export enum PkReturnError {
  SILENT_FAILURE = 'SILENT_FAILURE',
}

export enum AuthStep {
  START = 'START',

  SIGNIN_METHOD_SELECTOR = 'SIGNIN_METHOD_SELECTOR',
  SIGNIN_METHOD_EMAILOTP = 'SIGNIN_METHOD_EMAILOTP',
  SIGNIN_METHOD_PASSKEY = 'SIGNIN_METHOD_PASSKEY',
  SIGNIN_METHOD_PASSKEY_RETRY = 'SIGNIN_METHOD_PASSKEY_RETRY',
  SIGNIN_METHOD_PASSWORD = 'SIGNIN_METHOD_PASSWORD',
  SIGNIN_METHOD_UNAVAILABLE = 'SIGNIN_METHOD_UNAVAILABLE',

  SIGNIN_CREATE_PASSKEY_PROMPT = 'SIGNIN_CREATE_PASSKEY_PROMPT',
  SIGNIN_CREATE_PASSKEY_SUCCESS = 'SIGNIN_CREATE_PASSKEY_SUCCESS',
  SIGNIN_CREATE_PASSKEY_ERROR = 'SIGNIN_CREATE_PASSKEY_ERROR',
  SIGNIN_CREATE_PASSKEY_ERROR_TERMINAL = 'SIGNIN_CREATE_PASSKEY_ERROR_TERMINAL',

  SIGNUP_EMAIL_PROMPT = 'SIGNUP_EMAIL_PROMPT',
  SIGNUP_NAME_PROMPT = 'SIGNUP_NAME_PROMPT',
  SIGNUP_CONFIRM = 'SIGNUP_CONFIRM',
  SIGNIN_CREATE_PASSKEY_LOADING = 'SIGNIN_CREATE_PASSKEY_LOADING',
}

export enum AuthActionType {
  START_SIGNIN_SUCCESS = 'START_SIGNIN_SUCCESS',

  START_SIGNUP = 'START_SIGNUP',

  NEXT_STEP = 'NEXT_STEP',
  PREVIOUS_STEP = 'PREVIOUS_STEP',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_RETURN_URL = 'SET_RETURN_URL',
  SET_CSRF_TOKEN = 'SET_CSRF_TOKEN',

  UPDATE_SIGNUP_FIELD = 'UPDATE_SIGNUP_FIELD',

  SUCCESS = 'SUCCESS',

  SIGNIN_CHANGE_METHOD = 'SIGNIN_CHANGE_METHOD',
  SIGNIN_METHOD_PASSKEY_CANCEL = 'SIGNIN_METHOD_PASSKEY_CANCEL',
  SIGNIN_SUGGEST_PASSKEY = 'SIGNIN_SUGGEST_PASSKEY',
  SIGNIN_ENROLL_PASSKEY = 'SIGNIN_ENROLL_PASSKEY',
  SIGNIN_PASSKEY_CREATE_SUCCESS = 'SIGNIN_PASSKEY_CREATE_SUCCESS',
  SIGNIN_PASSKEY_CREATE_ERROR = 'SIGNIN_PASSKEY_CREATE_ERROR',
  SIGNIN_PASSKEY_CREATE_ERROR_TERMINAL = 'SIGNIN_PASSKEY_CREATE_ERROR_TERMINAL',

  SIGNUP_EDIT_EMAIL = 'SIGNUP_EDIT_EMAIL',
  SIGNUP_EDIT_NAME = 'SIGNUP_EDIT_NAME',
}

// designed to match the backend urls
export enum AuthMethodEnum {
  EMAIL_OTP = 'email_otp',
  PASSKEY = 'passkey',
  PASSWORD = 'password',
  UNAVAILABLE = 'unavailable',
}

export type AuthMethodEmailOTP = {
  type: AuthMethodEnum.EMAIL_OTP
  color: string
  timeout: number // in seconds
}

export type AuthMethodPasskey = {
  type: AuthMethodEnum.PASSKEY
  challenge: PublicKeyCredentialRequestOptionsJSON
}

export type AuthMethodOptions =
  | AuthMethodEmailOTP
  | AuthMethodPasskey
  | { type: AuthMethodEnum }

export interface AuthConfig {
  returnUrl: string
  logoUrl: string | null
  clubId: string | null
  customDomain: string | null
  recaptchaSiteKey: string | null
  csrfToken: string
  passkeyPrompt: string
  loginHint: string | null
  forcePkEnroll: boolean | null
  captchaBypass: boolean | null
  passkeyStorageId: string | null
}

export interface BaseAuthState {
  step: AuthStep
  error: string | null
  loading: boolean
  config: AuthConfig
  isDirty: boolean
}

export type AvatarImage = {
  type: 'image'
  url: string
}

export type AvatarInitials = {
  type: 'initials'
  initials: string
  color: string
}

export type SigninResponse = {
  newCsrf: string
  suggestPasskey: { suggest: boolean; prompt: string; storageId: string | null }
  passkeySignals?: PasskeySignals
}

export type PasskeySignals = {
  allAcceptedCredentialIds: string[]
  userId: string
  name: string
  displayName: string
}

export type AvatarType = AvatarImage | AvatarInitials

export interface SignInAuthState extends BaseAuthState {
  signin: {
    authMethod: AuthMethodOptions
    userInfo: {
      email: string
      displayName: string
      avatar: AvatarType
      authMethods: AuthMethodEnum[]
      requiresTermsAgreement: boolean
    }
    termsAgreed: boolean
  }
  signup: null
}

export interface SignUpAuthState extends BaseAuthState {
  signin: null
  signup: {
    email: string
    firstName: string
    lastName: string
    recaptcha: string
    passkey: string
  }
}

export type AuthState = SignInAuthState | SignUpAuthState

export type AuthSignInKeys = keyof AuthState['signin']
export type AuthSignUpKeys = keyof AuthState['signup']

export type contextNextStep = () => void
export type contextPreviousStep = () => void
export type contextStartSignin = (
  recaptca: Promise<string>,
  email: string,
) => void
export type contextSignupSubmit = (passkey?: string) => void
export type contextSigninSubmit = (
  authValue: any,
  handleErrors?: boolean,
) => void
export type contextEnrollPasskey = () => void
export type contextSigninWithPasskey = () => void
export type contextSigninWithAnonymousPasskey = () => void
export type contextChooseMethod = (chosenMethod: AuthMethodEnum) => void
export type contextRetryMethod = () => void

export type SigninArgs =
  | {
      authMethod: AuthMethodEnum.PASSKEY
      credential: AuthenticationResponseJSON
      termsAgreed?: boolean
    }
  | {
      authMethod: Exclude<AuthMethodEnum, AuthMethodEnum.PASSKEY>
      credential: string
      termsAgreed?: boolean
    }

export interface AuthHandlers {
  queryUser: (
    csrfToken: string,
    clubId: string | null,
    customDomain: string | null,
    recaptcha: string,
    email: string,
  ) => Promise<AuthState['signin']['userInfo']>
  signInUser: (csrfToken: string, args: SigninArgs) => Promise<SigninResponse>
  signUpUser: (
    csrfToken: string,
    email: string,
    firstName: string,
    lastName: string,
  ) => Promise<SigninResponse>
  enrollPasskeyChallenge: (
    csrfToken: string,
  ) => Promise<PublicKeyCredentialCreationOptionsJSON>
  enrollPasskey: (
    csrfToken: string,
    challenge: RegistrationResponseJSON,
  ) => Promise<{}>
  chooseMethod: (
    csrfToken: string,
    authMethod: AuthMethodEnum,
  ) => Promise<AuthMethodOptions>
  anonymousSigninPasskeyChallenge: (
    csrfToken: string,
    clubId: string | null,
    customDomain?: string | null,
  ) => Promise<{ challenge: PublicKeyCredentialRequestOptionsJSON }>
  anonymousSigninPasskey: (
    csrfToken: string,
    credential: any,
  ) => Promise<SigninResponse>
  browserSupportsWebAuthn: () => boolean
  browserSupportsWebAuthnAutofill: () => Promise<boolean>
  platformAuthenticatorIsAvailable: () => Promise<boolean>
  startAuthentication: (options: {
    optionsJSON: PublicKeyCredentialRequestOptionsJSON
    useBrowserAutofill?: boolean
    verifyBrowserAutofillInput?: boolean
  }) => Promise<AuthenticationResponseJSON>
  startRegistration: (options: {
    optionsJSON: PublicKeyCredentialCreationOptionsJSON
    useAutoRegister?: boolean
  }) => Promise<RegistrationResponseJSON>
  signalAllAcceptedCredentials: (
    userId: string,
    allAcceptedCredentialIds: string[],
  ) => void
  signalCurrentUserDetails: (
    userId: string,
    name: string,
    displayName: string,
  ) => void
  signalUnknownCredential: (rawId: string) => void
  fetchReturnUrl: (csrfToken: string) => Promise<string>
}

export interface AuthContextType {
  state: AuthState
  dispatch: React.Dispatch<AuthAction>
  nextStep: contextNextStep
  previousStep: contextPreviousStep
  startSignin: contextStartSignin
  chooseMethod: contextChooseMethod
  retryMethod: contextRetryMethod
  signupSubmit: contextSignupSubmit
  signinSubmit: contextSigninSubmit
  enrollPasskey: contextEnrollPasskey
  signinWithPasskey: contextSigninWithPasskey
  signinWithAnonymousPasskey: contextSigninWithAnonymousPasskey
}

type ExplicitAuthActions =
  | {
      type: AuthActionType.START_SIGNIN_SUCCESS
      chosenMethod: AuthMethodOptions
      userInfo: SignInAuthState['signin']['userInfo']
    }
  | { type: AuthActionType.START_SIGNUP; email: string }
  | {
      type: AuthActionType.SIGNIN_CHANGE_METHOD
      chosenMethod: AuthMethodOptions | null
    }
  | { type: AuthActionType.SET_ERROR; message: string }
  | { type: AuthActionType.SET_RETURN_URL; url: string }
  | { type: AuthActionType.SET_CSRF_TOKEN; token: string }
  | {
      type: AuthActionType.UPDATE_SIGNUP_FIELD
      field: AuthSignUpKeys
      value: string
    }
  | { type: AuthActionType.SIGNIN_PASSKEY_CREATE_ERROR; message: string }
  | {
      type: AuthActionType.SIGNIN_PASSKEY_CREATE_ERROR_TERMINAL
      message: string
    }
  | { type: AuthActionType.SIGNIN_METHOD_PASSKEY_CANCEL; message: string }

// Actions that don't require parameters (allowed by default)
type ImplicitAuthActions = {
  type: Exclude<AuthActionType, ExplicitAuthActions['type']>
}

// Combine both
export type AuthAction = ExplicitAuthActions | ImplicitAuthActions

export type onMethodChange = (
  method: AuthMethodEnum,
) => (e: React.SyntheticEvent<HTMLElement>) => void
export type eventHandler = (e: React.SyntheticEvent<HTMLElement>) => void
